import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {
    AnyImageOpContext,
    AnyImageOpResult,
    AnyImageOpResultOrNode,
    ImageOpOutputs,
    ValueOrNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeCreateImage} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/create-image/node"
import {Vector2Like} from "@cm/math"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/clone-stamp/node/definition"
import {ImageOpNodeBlit} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blit/node"
import {ImageOpNodeExtractRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/node"
import {ImageOpNodeBlend} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blend/node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {CompositeImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/composite-image-op-node"

@registerNode
export class ImageOpNodeOperatorCloneStamp extends CompositeImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeOperatorCloneStamp"
    static definition = definition

    override async execute(evaluator: Evaluator<AnyImageOpContext>, imageOpNodeBag: ImageOpNodeBag): Promise<ImageOpOutputs<typeof definition>> {
        const {sourceImage: sourceImageIn, strokes, drawMaskOnly} = await evaluator.getAll(this.parameters)

        let sourceImage: AnyImageOpResultOrNode
        let resultImage: ValueOrNode<AnyImageOpResult>
        if (drawMaskOnly) {
            sourceImage = imageOpNodeBag.getNode("constantWhiteImage", ImageOpNodeCreateImage, {
                descriptor: sourceImageIn.descriptor,
                data: 1,
            })
            resultImage = imageOpNodeBag.getNode("constantBlackImage", ImageOpNodeCreateImage, {
                descriptor: sourceImageIn.descriptor,
                data: 0,
            })
        } else {
            sourceImage = sourceImageIn
            resultImage = sourceImageIn
        }
        const applyStroke = (
            prefix: string,
            maskImage: AnyImageOpResult,
            maskOffsetInPixels: Vector2Like,
            sourceImage: ValueOrNode<AnyImageOpResult>,
            sourceOffsetInPixels: Vector2Like,
            resultImage: ValueOrNode<AnyImageOpResult>,
            // Safe only when `resultImage` is this loop's own previous-stroke output, not the operator's original
            // input - the input may still be referenced elsewhere in the wider graph.
            canMutateResultImageInPlace: boolean,
        ): ValueOrNode<AnyImageOpResult> => {
            const cutoutShiftedSourceImage = imageOpNodeBag.getNode(prefix + "ShiftedSourceImageCutout", ImageOpNodeExtractRegion, {
                sourceImage: sourceImage,
                region: {
                    x: sourceOffsetInPixels.x,
                    y: sourceOffsetInPixels.y,
                    width: maskImage.descriptor.width,
                    height: maskImage.descriptor.height,
                },
                addressMode: "wrap",
            })
            const cutoutResultImage = imageOpNodeBag.getNode(prefix + "ResultImageCutout", ImageOpNodeExtractRegion, {
                sourceImage: resultImage,
                region: {
                    x: maskOffsetInPixels.x,
                    y: maskOffsetInPixels.y,
                    width: maskImage.descriptor.width,
                    height: maskImage.descriptor.height,
                },
                addressMode: "wrap",
            })
            const stampedRegion = imageOpNodeBag.getNode(prefix + "StampedRegion", ImageOpNodeBlend, {
                backgroundImage: cutoutResultImage,
                foregroundImage: cutoutShiftedSourceImage,
                alpha: maskImage,
                premultipliedAlpha: false,
                blendMode: "over",
            })
            return imageOpNodeBag.getNode(prefix + "BlitStampedRegion", ImageOpNodeBlit, {
                backgroundImage: resultImage,
                foregroundImage: stampedRegion,
                position: {
                    x: maskOffsetInPixels.x,
                    y: maskOffsetInPixels.y,
                },
                outOfBoundsHandling: "clip", // TODO put this back to "wrap" ?
                inPlace: canMutateResultImageInPlace,
            })
            // return imageOpNodeBag.getNode(prefix + "ResultImage", ImageOpNodeBlit, {
            //     backgroundImage: resultImage,
            //     foregroundImage: maskImage,
            //     position: {
            //         x: maskOffsetInPixels.x,
            //         y: maskOffsetInPixels.y,
            //     },
            //     addressMode: "wrap",
            // })
            // return imageOpNodeBag.getNode(prefix + "ResultImage", ImageOpNodeBlend, {
            //     backgroundImage: resultImage,
            //     foregroundImage: maskImage,
            //     blendMode: "add",
            //     // foregroundImage: sourceImage,
            //     // alpha: maskImage,
            //     // premultipliedAlpha: false,
            //     // blendMode: "over",
            // })
        }
        const modePrefix = drawMaskOnly ? "mask_" : ""
        strokes.forEach((stroke, index) => {
            // Stroke 0's resultImage is the operator's original input (or the synthetic mask baseline), which may
            // be referenced elsewhere in the graph - only strokes after it consume a result this loop itself made.
            resultImage = applyStroke(
                modePrefix + `stroke${index}`,
                stroke.maskImage,
                stroke.maskOffset,
                sourceImage,
                stroke.sourceOffset,
                resultImage,
                index > 0,
            )
        })

        return await evaluator.get(resultImage)
    }
}
